import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Button from 'components/common/Button';
import FileUpload from 'components/common/FileUpload';
import { EMPTY_FILE, Wrapper } from 'pages/Candidate/Offer/styles';
import { svgs } from 'assets';
import { useToast } from 'context/toastContext';
import { rejectEmptyFile } from 'utils/emptyFile';
import {
  submitTraineeOfferChecklist,
  getTraineeOfferChecklistStatus,
} from 'redux/actions/offer';

// Mirrors candidate_onboarding.models.TRAINEE_OFFER_CHECKLIST on the backend
// -- field name (and required-ness) must match what the API expects for
// each item.
const CHECKLIST = [
  { name: 'biodata_form', label: 'Trainee Biodata Form', required: true },
  { name: 'proof_of_address', label: 'Proof of address', required: true },
  { name: 'cv', label: 'Curriculum Vitae (CV)', required: true },
  { name: 'professional_headshot', label: 'Professional headshot', required: true },
  { name: 'ssce_certificate', label: 'SSCE certificate', required: true },
  { name: 'tertiary_certificate', label: 'Tertiary qualification certificate', required: true },
  { name: 'nysc_certificate', label: 'NYSC discharge or exemption certificate', required: true },
  { name: 'birth_certificate', label: 'Birth certificate', required: true },
  { name: 'government_id', label: 'Government-issued ID', required: true },
  { name: 'signed_offer_letter', label: 'Signed offer letter acceptance', required: true },
  { name: 'guarantor_undertaking', label: "Guarantor's Undertaking", required: false },
  {
    name: 'signed_handbook_acknowledgement',
    label: 'Signed Handbook acknowledgement',
    required: false,
  },
];

export const TraineeOfferChecklist = ({ offerId, firstname }) => {
  const history = useHistory();
  const toast = useToast();
  const [files, setFiles] = useState({});
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);

  useEffect(() => {
    if (!offerId) return;
    (async () => {
      try {
        const response = await getTraineeOfferChecklistStatus(offerId);
        setAlreadySubmitted(!!response?.data?.submitted);
      } catch (error) {
        // If the status check itself fails, fall back to showing the form --
        // the submit endpoint still enforces the one-shot rule server-side.
      } finally {
        setCheckingStatus(false);
      }
    })();
  }, [offerId]);

  const handleChange = (name) => (e) => {
    e.persist();
    if (rejectEmptyFile(e, toast)) return;
    setFiles((prev) => ({ ...prev, [name]: e.target.files[0] }));
  };

  const missingItems = CHECKLIST.filter((item) => item.required && !files[item.name]);
  const allRequiredFilesPresent = missingItems.length === 0;

  const handleSubmit = async () => {
    if (!allRequiredFilesPresent) {
      toast.error(`Please upload: ${missingItems.map((item) => item.label).join(', ')}`);
      return;
    }

    const formData = new FormData();
    CHECKLIST.forEach((item) => {
      if (files[item.name]) formData.append(item.name, files[item.name]);
    });

    setLoading(true);
    try {
      await submitTraineeOfferChecklist(offerId, formData);
      toast.success('Documents submitted successfully');
      history.push({ pathname: '/onboarding/offer-status', state: { trainee: true } });
    } catch (error) {
      if (error?.response?.status === 409) {
        setAlreadySubmitted(true);
      }
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.errors ||
          'Could not submit your documents, please try again'
      );
    } finally {
      setLoading(false);
    }
  };

  if (checkingStatus) {
    return (
      <Wrapper>
        <div className="content">
          <p className="header">Checking your submission status&hellip;</p>
        </div>
      </Wrapper>
    );
  }

  if (alreadySubmitted) {
    return (
      <Wrapper>
        <div className="content">
          <div className="logo-wrapper">
            <EMPTY_FILE />
          </div>
          <p className="header">Documents already submitted</p>
          <p className="note">
            Hello, <span>{firstname}</span>. You have already submitted your onboarding
            documents for this offer. There is nothing further to do here.
          </p>
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <div className="content">
        <div className="logo-wrapper">
          <EMPTY_FILE />
        </div>
        <p className="header">Submit your onboarding documents</p>
        <p className="note">
          Hello, <span>{firstname}</span>. All of the documents below are required to complete
          your enrolment. You will only be able to submit once.
        </p>

        {CHECKLIST.map((item) => (
          <div className="file-upload-wrapper" key={item.name} style={{ marginBottom: 20 }}>
            <FileUpload
              className="upload"
              limit={25}
              acceptedFiles="application/pdf,.docx,image/*"
              label={item.required ? `${item.label} *` : `${item.label} (optional)`}
              name={item.name}
              value={files[item.name]}
              icon={svgs.UPLOAD_ICON_CLOUD}
              onChange={handleChange(item.name)}
            />
          </div>
        ))}
      </div>
      <div className="footer">
        <Button
          text={loading ? 'Submitting...' : 'Submit Documents'}
          className="button"
          disabled={loading || !allRequiredFilesPresent}
          onClick={handleSubmit}
        />
      </div>
    </Wrapper>
  );
};
